import { Card } from "@/components/Card";
import type { PairLatency } from "@/lib/api";
import { useProfileLabel } from "@/lib/profileLabels";

interface LatencyProps {
  profileA: string;
  profileB: string;
  latencyA: PairLatency;
  latencyB: PairLatency;
}

/**
 * Rolling latency stats card: one row per profile (mean / p50 / p95 /
 * samples) pulled from the aggregate doc's running sums + sum-of-squares.
 */
export function Latency({ profileA, profileB, latencyA, latencyB }: LatencyProps) {
  const label = useProfileLabel();
  return (
    <Card style={{ padding: "14px 22px", marginBottom: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          marginBottom: 4,
        }}
      >
        <div className="overline">Latency</div>
        <div style={{ fontSize: 11, lineHeight: 1.2, color: "var(--muted-foreground)" }}>
          Computed over all queries.
        </div>
      </div>
      <LatencyRow profile={label(profileA)} color="var(--atf-pink-600)" data={latencyA} />
      <div style={{ height: 1, background: "var(--border)" }} />
      <LatencyRow profile={label(profileB)} color="var(--atf-blue-950)" data={latencyB} />
    </Card>
  );
}

function LatencyRow({
  profile,
  color,
  data,
}: {
  profile: string;
  color: string;
  data: PairLatency;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "10px 0",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, width: 160 }}>
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            background: color,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          {profile}
        </span>
      </div>
      <Stat label="mean" value={`${data.mean_ms}`} unit="ms" />
      <Stat label="p50" value={`${data.p50_ms}`} unit="ms" />
      <Stat label="p95" value={`${data.p95_ms}`} unit="ms" />
      <Stat label="samples" value={`${data.count}`} />
    </div>
  );
}

function Stat({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  unit?: string;
}) {
  return (
    <div style={{ minWidth: 72 }}>
      <div
        style={{
          fontSize: 10,
          color: "var(--muted-foreground)",
          fontFamily: "var(--font-mono)",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          fontFamily: "var(--font-mono)",
        }}
      >
        {value}
        {unit && (
          <span
            style={{
              fontSize: 11,
              color: "var(--muted-foreground)",
              marginLeft: 2,
              fontWeight: 500,
            }}
          >
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}
